import {
  catchError,
  firstValueFrom,
  Observable,
  of,
  Subject,
  Subscription,
  tap,
  throwError,
} from 'rxjs';
import { DateTime } from 'luxon';
import { toLuxon } from '@app/helper';
import { Injectable, Injector, OnDestroy } from '@angular/core';
import {
  ChatDiscussionAddedEvent,
  ChatMessageAddedEvent,
  ChatMessageEditEvent,
  ChatMessageEndEvent,
  ChatMessageReplyEvent,
  ChatMessageSentEvent,
} from './event';
import { updateListItems } from './helper/update-list-items';
import { ChatDiscussion, ChatMessage } from './entity';
import { ChatDiscussionSwitchedEvent } from './event/discussion-switched';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ApiAuthService, AuthLastActivityUpdatedEvent } from '@app/api';
import { AppNotifyService } from '@app/notify';

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export interface AbstractChatService {
  discussionLoader?(data: any): Observable<any>;
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
@Injectable()
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export abstract class AbstractChatService implements OnDestroy {
  discussion: any = null;
  discussionSubject: any = new Subject();

  end = false;
  discussionEnd = false;
  messages: any[] = [];
  discussions: any[] = [];
  replyMessage: ChatMessage | null = null;
  editMessage: ChatMessage | null = null;

  abstract user: any;
  loadSubscription?: Subscription;
  removeSubscription?: Subscription;
  private _pullLast: DateTime | null = DateTime.now();
  private _pullFirst = true;
  private _pullSubscription?: Subscription;
  private readonly _events: Subject<any>;
  private _discussionViewedSubscription?: Subscription;
  private _lastViewed = false;

  private _subscriptions: Subscription[] = [];

  protected constructor(private _injector: Injector) {
    this._events = new Subject();

    this._subscriptions.push(
      this.discussionSubject.subscribe(() => this.reset()),
    );

    this._subscriptions.push(
      (_injector.get(ApiAuthService) as ApiAuthService).events.subscribe(
        (e) => {
          if (e instanceof AuthLastActivityUpdatedEvent) {
            this._setDiscussionViewed();
          }
        },
      ),
    );
  }

  get events() {
    return this._events;
  }

  ngOnDestroy() {
    this._subscriptions?.forEach((s) => s.unsubscribe());
    this._subscriptions = [];
    this._clearPulling();
  }

  reset() {
    this.messages = [];
    this.end = false;
    this.loadSubscription?.unsubscribe();
    this.removeSubscription?.unsubscribe();
    if (this.discussion && !this._pullSubscription) {
      this.pull();
    }
  }

  abstract loader(data: any): Observable<any>;

  discussionLoader?(data: any): Observable<any>;

  discussionViewer?(data: any): Observable<any>;

  abstract sender(data: any): Observable<any>;

  messageRemover?(data: any): Observable<any>;

  abstract getUserPictureUrl(user: any): string;

  abstract getUserPictureBackground(user: any): any;

  remover?(data: any): Observable<any>;

  canRemove() {
    return !!this.remover;
  }

  abstract puller(data: any): Observable<any>;

  abstract uploader(data: any): Observable<any>;

  abstract muter(data: any, mute: boolean): Observable<any>;

  messageFormatter(data: any) {
    return data;
  }

  discussionFormatter(data: any) {
    return data;
  }

  setDiscussion(discussion: any) {
    if (discussion?.id !== this.discussion?.id) {
      this.discussion = new ChatDiscussion(discussion);
      this.discussionSubject.next(this.discussion);
      this._events.next(new ChatDiscussionSwitchedEvent(this.discussion));
    }
  }

  addDiscussions(discussions: any[]) {
    discussions = discussions.map((data) => this.discussionFormatter(data));

    const [finalDiscussions, newDiscussions] = updateListItems(
      this.discussions,
      discussions,
      (data: any) => new ChatDiscussion(data),
    );
    this.discussions = finalDiscussions;
    this._events.next(new ChatDiscussionAddedEvent(newDiscussions));
  }

  addMessages(messages: any[]) {
    messages = messages.map((data) => this.messageFormatter(data));

    const [finalMessages, newMessages] = updateListItems(
      this.messages,
      messages,
      (data: any) => new ChatMessage(data),
    );
    this.messages = finalMessages;
    this._events.next(new ChatMessageAddedEvent(newMessages));
    this._lastViewed = false;
  }

  send(data: any) {
    return firstValueFrom(
      this.sender({
        ...data,
        replyTo: this.replyMessage?.id,
        message: this.editMessage?.id,
      }).pipe(
        tap((result: any) => {
          if (result.data) {
            const edit = !!this.editMessage?.id;
            if (!this.discussion) {
              this.setDiscussion(result.data.discussion);
            }
            this.replyMessage && this.messageReply(null);
            this.editMessage && this.messageEdit(null);
            this.addDiscussions([result.meta.discussion]);
            this.addMessages([result.data]);

            this._events.next(new ChatMessageSentEvent(result.data, edit));
          }
        }),
        catchError((error) => {
          if (error.data?.user == 'muted') {
            (this._injector.get(AppNotifyService) as AppNotifyService).error(
              'Vous avez été mis en sourdine et ne pouvez donc plus envoyer de message pour le moment.',
            );
            return of(null);
          } else {
            return throwError(() => error);
          }
        }),
      ),
    ) as Promise<void>;
  }

  remove() {
    this.remover?.({ id: this.discussion?.id }).subscribe();
  }

  loadDiscussions() {
    if (this.discussionEnd || !this.discussionLoader) {
      return;
    }
    return firstValueFrom(
      this.discussionLoader({
        size: 10,
        offset: this.discussions.length,
      }).pipe(
        tap((result: any) => {
          if (result.data.length) {
            this.addDiscussions(result.data);
          } else {
            this.discussionEnd = true;
          }
        }),
      ),
    );
  }

  load() {
    if (this.end || !this.discussion) {
      return;
    }
    return new Promise<any>((resolve, reject) => {
      this.loadSubscription = this.loader({
        size: 10,
        offset: this.messages.length,
        sort: [
          ['created', 'desc'],
          ['id', 'desc'],
        ],
      }).subscribe({
        next: (result: any) => {
          if (result.data.length) {
            this.addMessages(result.data);
          } else {
            this.end = true;
            this._events.next(new ChatMessageEndEvent());
          }
          resolve(result);
        },
        error: (e) => reject(e),
      });
    });
  }

  removeMessage(message: any) {
    return new Promise<any>((resolve, reject) => {
      this.removeSubscription = this.messageRemover?.(message).subscribe({
        next: (result: any) => {
          if (result.data.length) {
            this.addMessages([result.data]);
          }
          resolve(result);
        },
        error: (e) => reject(e),
      });
    });
  }

  messageMute(message: any, mute: boolean) {
    return new Promise<any>((resolve, reject) => {
      this.removeSubscription = this.muter?.(message, mute).subscribe({
        next: (result: any) => {
          if (result.data.length) {
            this.addMessages([result.data]);
          }
          resolve(result);
        },
        error: (e) => reject(e),
      });
    });
  }

  messageReply(message: any) {
    this.replyMessage = message;
    this.events.next(new ChatMessageReplyEvent(message));
  }

  messageEdit(message: any) {
    this.editMessage = message;
    this.events.next(new ChatMessageEditEvent(message));
  }

  pull() {
    this._clearPulling();
    console.log('PULL ', this._pullLast, this._pullFirst);
    this._pullSubscription = this.puller({
      after: this._pullLast,
      first: this._pullFirst,
    }).subscribe({
      next: (result: any) => {
        if (result.data.messages && result.data.messages.length > 0) {
          const messages = result.data.messages.filter(
            (m: any) => m.discussion?.id == this.discussion?.id,
          );
          if (messages.length > 0) {
            this.addMessages(result.data.messages);
          }
        }
        this.addDiscussions(result?.data?.discussions);

        this._pullLast = toLuxon(result.data.pulled.date);
        this._pullFirst = false;
        setTimeout(() => this.pull(), 500);
      },
      error: (error: any) => {
        console.log('PULL ERROR', error);
        setTimeout(() => this.pull(), 500);
      },
    });
  }

  private _setDiscussionViewed() {
    if (
      !this.discussionViewer ||
      this._discussionViewedSubscription ||
      !this.discussion ||
      this._lastViewed
    ) {
      return;
    }

    const lastActivityDiff = (
      this._injector.get(ApiAuthService) as ApiAuthService
    ).lastActivity
      ?.diff(DateTime.now())
      .get('seconds');

    if (!lastActivityDiff || lastActivityDiff >= 0) {
      this._discussionViewedSubscription = this.discussionViewer({
        discussion: this.discussion.id,
      }).subscribe((result) => {
        this._discussionViewedSubscription?.unsubscribe();
        delete this._discussionViewedSubscription;
        this._lastViewed = true;
      });
    }
  }

  private _clearPulling() {
    if (this._pullSubscription) {
      this._pullSubscription.unsubscribe();
      delete this._pullSubscription;
    }
  }
}
